import { MatrixClient, Preset } from 'matrix-js-sdk/src/matrix';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useMessengerRoomCreation from 'hooks/roomCreation/useMessengerRoomCreation';
import useRoomAccessInitialState, { RoomAccessType } from 'hooks/roomCreation/useRoomAccessInitialState';
import useRoomCreationPowerLevels from 'hooks/roomCreation/useRoomCreationPowerLevels';
import useRoomCreationValidator, { BotValidatedState } from 'hooks/roomCreation/useRoomCreationValidator';
import useRoomManagersInitialState from 'hooks/roomCreation/useRoomManagersInitialState';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import { getIsMatrixError, MatrixErrorEnum } from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/utils';
import useMatrixInviteeValidation from 'modules/Messenger/hooks/useMatrixInviteeValidation';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { APP_ROUTES } from 'routes/routePaths';
import { logAndSendToSentry } from 'utils/utils';

import { ChatPrivacyEnum } from './ChatPrivacyField/ChatPrivacyEnum';
import { NewChatFormType } from './CreateNewChatModal';

const chatPrivacyToAccessType = (chatPrivacy: ChatPrivacyEnum): RoomAccessType =>
  chatPrivacy === ChatPrivacyEnum.PRIVATE ? 'team' : 'shared';

type RejectToast = {
  variant: 'error' | 'warning';
  title: string;
  description?: string;
};

interface UseCreateMessengerRoomReturn {
  createMessengerRoom: (form: NewChatFormType) => Promise<boolean>;
  retryCreate: () => Promise<boolean>;
  resetCreate: () => void;
  createState: BotValidatedState;
  isLoading: boolean;
}

const getLinkFailureRejectToast = (
  form: NewChatFormType,
  error: unknown,
  t: (key: string) => string,
  matrixClient: MatrixClient,
  workspaceId: string
): RejectToast => {
  const permissions = calculatePermissions(matrixClient, workspaceId);

  if (form.chatPrivacy === ChatPrivacyEnum.PUBLIC && !permissions.invitePeople) {
    return { variant: 'error', title: t('room_settings.errors.workspace_pl_required') };
  }

  if ((getIsMatrixError(error) && error.errcode === MatrixErrorEnum.M_FORBIDDEN) || !permissions.createRoom) {
    return {
      variant: 'warning',
      title: t('workspaces.no_permission'),
      description: t('workspaces.no_permission_description'),
    };
  }

  return { variant: 'error', title: t('something_went_wrong_try_again_later') };
};

const useCreateMessengerRoom = (): UseCreateMessengerRoomReturn => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const { createMessengerRoom: createMessengerRoomCore } = useMessengerRoomCreation();
  const { matrixClient } = useMatrixContext();
  const { workspaceId } = useWorkspacesContext();
  const navigate = useNavigate();
  const { filterInvalidMatrixInvitees } = useMatrixInviteeValidation();

  const roomManagersInitialState = useRoomManagersInitialState(workspaceId);
  const { powerLevelOverrides: chatPowerLevelOverrides, ownerPowerLevel: chatOwnerPowerLevel } =
    useRoomCreationPowerLevels('chat');
  const teamRoomAccessInitialState = useRoomAccessInitialState('team');
  const sharedRoomAccessInitialState = useRoomAccessInitialState('shared');

  const pendingFormRef = useRef<NewChatFormType | null>(null);
  const createdRoomIdRef = useRef<string | null>(null);
  const rejectToastRef = useRef<RejectToast | null>(null);
  const succeededRef = useRef(false);

  const runCreation = useCallback(async () => {
    const form = pendingFormRef.current;
    if (!form) throw new Error('useCreateMessengerRoom: no pending form');

    let filteredInvitees = form.invitees;

    try {
      filteredInvitees = await filterInvalidMatrixInvitees(filteredInvitees);
    } catch (error) {
      logAndSendToSentry('Failed to validate Matrix invitees', { error });
      rejectToastRef.current = { variant: 'error', title: t('something_went_wrong_try_again_later') };
      throw error;
    }

    const preset = form.chatPrivacy === ChatPrivacyEnum.PRIVATE ? Preset.PrivateChat : Preset.PublicChat;
    const accessType = chatPrivacyToAccessType(form.chatPrivacy);
    const roomAccessEvent = accessType === 'team' ? teamRoomAccessInitialState : sharedRoomAccessInitialState;

    // The shared core assembles initial_state (incl. encryption under the flag), creates the room
    // via useCreateRoom, registers the workspace SpaceChild (rollback + throw on failure), and
    // invites participants. Caller-specific concerns (chat-scope power levels, team/shared access
    // from chat privacy, navigation, permission toasts, invitee filtering) stay in this wrapper.
    let roomId: string;
    let failedInvites: string[];

    try {
      ({ roomId, failedInvites } = await createMessengerRoomCore({
        name: form.chatName,
        participantMxids: filteredInvitees,
        preset,
        workspaceId,
        ...(workspaceId
          ? {
              roomManagersInitialState,
              roomAccessInitialState: roomAccessEvent,
              powerLevelOverrides: chatPowerLevelOverrides,
              ownerPowerLevel: chatOwnerPowerLevel,
            }
          : {}),
      }));
    } catch (error) {
      rejectToastRef.current = getLinkFailureRejectToast(form, error, t, matrixClient, workspaceId ?? '');
      throw error;
    }

    createdRoomIdRef.current = roomId;

    if (failedInvites.length === filteredInvitees.length && filteredInvitees.length > 0) {
      pushToast({ variant: 'error', title: t('messenger.all_invites_failed') });
    } else if (failedInvites.length > 0) {
      pushToast({
        variant: 'warning',
        title: t('messenger.some_users_not_invited', { users: failedInvites.join(', ') }),
      });
    }

    return roomId;
  }, [
    chatPowerLevelOverrides,
    chatOwnerPowerLevel,
    createMessengerRoomCore,
    filterInvalidMatrixInvitees,
    matrixClient,
    pushToast,
    roomManagersInitialState,
    sharedRoomAccessInitialState,
    t,
    teamRoomAccessInitialState,
    workspaceId,
  ]);

  const handleSuccess = useCallback(() => {
    succeededRef.current = true;
    const roomId = createdRoomIdRef.current;
    if (roomId) navigate(`${APP_ROUTES.messenger}/${roomId}`);
  }, [navigate]);

  const handleReject = useCallback(() => {
    succeededRef.current = false;
    const toast = rejectToastRef.current ?? { variant: 'error', title: t('something_went_wrong_try_again_later') };
    pushToast(toast);
    rejectToastRef.current = null;
  }, [pushToast, t]);

  const {
    state: createState,
    execute,
    reset: resetCreate,
  } = useRoomCreationValidator({
    action: runCreation,
    onSuccess: handleSuccess,
    onReject: handleReject,
  });

  const hasCreatePermission = useCallback(
    (form: NewChatFormType): boolean => {
      if (!workspaceId) return true;

      const permissions = calculatePermissions(matrixClient, workspaceId);

      if (!permissions.createRoom) {
        pushNoPermissionToast();
        return false;
      }

      if (form.chatPrivacy === ChatPrivacyEnum.PUBLIC && !permissions.invitePeople) {
        pushToast({ variant: 'error', title: t('room_settings.errors.workspace_pl_required') });
        return false;
      }

      return true;
    },
    [matrixClient, pushNoPermissionToast, pushToast, t, workspaceId]
  );

  const createMessengerRoom = useCallback(
    async (form: NewChatFormType): Promise<boolean> => {
      if (createState === 'pending') return false;
      if (!hasCreatePermission(form)) return false;

      pendingFormRef.current = form;
      createdRoomIdRef.current = null;
      rejectToastRef.current = null;
      succeededRef.current = false;

      try {
        await execute();
      } catch (error) {
        logAndSendToSentry('Failed to create messenger room', { error });
      }

      return succeededRef.current;
    },
    [createState, execute, hasCreatePermission]
  );

  const retryCreate = useCallback(async (): Promise<boolean> => {
    if (!pendingFormRef.current) return false;
    if (createState === 'pending') return false;

    rejectToastRef.current = null;
    succeededRef.current = false;

    try {
      await execute();
    } catch (error) {
      logAndSendToSentry('Failed to create messenger room (retry)', { error });
    }

    return succeededRef.current;
  }, [createState, execute]);

  return {
    createMessengerRoom,
    retryCreate,
    resetCreate,
    createState,
    isLoading: createState === 'pending',
  };
};

export default useCreateMessengerRoom;
